
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Plus, Trash2, User, Package, AlertTriangle } from "lucide-react";
import { format } from "date-fns";
import { useLocalization } from '../providers/LocalizationProvider';

const getInitialFormState = () => ({
  customer_id: '',
  customer_name: '',
  customer_email: '',
  items: [{ id: Date.now(), product_id: '', product_name: '', quantity: 1, unit_price: 0, total_price: 0 }],
  order_date: format(new Date(), 'yyyy-MM-dd'),
  payment_method: '',
  shipping_address: {
    street: '',
    city: '',
    state: '',
    postal_code: '',
    country: ''
  },
  shipping_cost: 0,
  notes: '',
  signature_required: false
});


export default function CreateOrderModal({
  isOpen, onClose, onSubmit, customers = [] }) {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState(getInitialFormState());
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isNewCustomer, setIsNewCustomer] = useState(false);
  const { paymentMethods, currencySymbol } = useLocalization();
  const normalizedPaymentMethods = Array.isArray(paymentMethods)
    ? paymentMethods
        .filter(m => m && m.is_active !== false)
        .map(m => ({
          value: (m.code || m.key || m.id || '').toString(),
          label: m.label || m.name || m.title || (m.code || m.key || m.id || 'Unknown')
        }))
        .filter(m => m.value.trim() !== '')
    : [];
  
  // Debug logging
  useEffect(() => {
    if (paymentMethods) {
      console.log('Payment methods:', paymentMethods);
    }
  }, [paymentMethods]);
  
  useEffect(() => {
    if (isOpen) {
        import('@/api/entities').then(({ Product }) => {
            Product.list().then(products => {
                // Filter out any invalid products
                const validProducts = products ? products.filter(p => p && p.id) : [];
                setProducts(validProducts);
            }).catch(error => {
                console.error('Error loading products:', error);
                setProducts([]);
            });
        });
        // Автовыбор метода оплаты, если не выбран
        if (!formData.payment_method && normalizedPaymentMethods.length > 0) {
          setFormData(prev => ({ ...prev, payment_method: normalizedPaymentMethods[0].value }));
        }
    } else {
        // Reset form when closing
        setFormData(getInitialFormState());
        setSelectedCustomer(null);
        setProducts([]);
    }
  }, [isOpen, normalizedPaymentMethods.length]);

  const handleCustomerSelect = (value) => {
    if (value === '__new__') {
      setIsNewCustomer(true);
      setSelectedCustomer(null);
      setFormData(prev => ({
        ...prev,
        customer_id: '',
        customer_name: prev.customer_name || '',
        customer_email: prev.customer_email || ''
      }));
      return;
    }
    const customer = customers.find(c => c.id === value);
    if (customer) {
      setIsNewCustomer(false);
      setSelectedCustomer(customer);
      setFormData(prev => ({
        ...prev,
        customer_id: customer.id,
        customer_name: customer.full_name,
        customer_email: customer.email,
        shipping_address: customer.shipping_address || prev.shipping_address
      }));
    }
  };

  const handleProductSelect = (itemId, productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      const newItems = formData.items.map(item => 
        item.id === itemId 
          ? {
              ...item,
              product_id: product.id,
              product_name: product.name,
              unit_price: product.price,
              total_price: product.price * item.quantity
            }
          : item
      );
      setFormData(prev => ({ ...prev, items: newItems }));
    }
  };

  const handleQuantityChange = (itemId, quantity) => {
    const newQuantity = parseInt(quantity) || 0;
    const newItems = formData.items.map(item => 
      item.id === itemId 
        ? {
            ...item,
            quantity: newQuantity,
            total_price: item.unit_price * newQuantity
          }
        : item
    );
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { id: Date.now(), product_id: '', product_name: '', quantity: 1, unit_price: 0, total_price: 0 }]
    }));
  };

  const removeItem = (itemId) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
  };

  const calculateTotal = () => {
    const itemsTotal = formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0);
    return itemsTotal + (formData.shipping_cost || 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Преобразуем данные товаров для соответствия API
      const formattedItems = formData.items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity
        // Цена будет получена на бэкенде из базы данных
      }));

      const orderData = {
        ...formData,
        items: formattedItems,
        total_amount: calculateTotal(),
        order_number: `ORD-${Date.now()}`,
        status: 'ordered'
      };

      // Если не выбран customer_id, но введены имя и email — позволяем создать
      if (!orderData.customer_id && orderData.customer_email && orderData.customer_name) {
        // backend выполнит ensureCustomer по email/name
      }

      await onSubmit(orderData);
      
      // Reset form and close
      setFormData(getInitialFormState());
      setSelectedCustomer(null);
      onClose();

    } catch (error) {
      console.error('Error creating order:', error);
    }
    
    setIsSubmitting(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-slate-900">Create New Order</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-slate-600" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="customer">{t('orders.create.select_customer')}</Label>
                <Select onValueChange={handleCustomerSelect} value={selectedCustomer?.id || (isNewCustomer ? '__new__' : undefined)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a customer or create new" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__new__">+ Create new customer</SelectItem>
                    {customers && Array.isArray(customers) && customers.filter(customer => customer && customer.id && customer.id.toString().trim() !== '').length > 0 ? customers.filter(customer => customer && customer.id && customer.id.toString().trim() !== '').map((customer) => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.full_name || customer.name || 'Unknown Customer'} - {customer.email || 'No email'}
                      </SelectItem>
                    )) : (
                      <SelectItem value="no-customers" disabled>No customers available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* New customer fields */}
              {(isNewCustomer || !selectedCustomer) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="customer_name">Customer Name</Label>
                    <Input
                      id="customer_name"
                      value={formData.customer_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                      placeholder="Full name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="customer_email">Customer Email</Label>
                    <Input
                      id="customer_email"
                      type="email"
                      value={formData.customer_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                      placeholder="email@example.com"
                    />
                  </div>
                </div>
              )}

              {selectedCustomer && (
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm font-medium text-slate-500">Name</p>
                    <p className="font-semibold">{selectedCustomer.full_name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-500">Email</p>
                    <p className="font-semibold">{selectedCustomer.email}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-slate-600" />
                  Order Items
                </div>
                <Button type="button" onClick={addItem} variant="outline" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Item
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {(formData.items || []).map((item, index) => (
                <div key={item.id || `item-${index}`} className="grid grid-cols-12 gap-4 items-end p-4 border border-slate-200 rounded-lg">
                  <div className="col-span-5">
                    <Label>{t('orders.create.product')}</Label>
                    <Select onValueChange={(productId) => handleProductSelect(item.id, productId)} value={item.product_id || undefined}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products && Array.isArray(products) && products.filter(product => product && product.id && product.id.toString().trim() !== '').length > 0 ? products.filter(product => product && product.id && product.id.toString().trim() !== '').map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name || 'Unknown Product'} - {currencySymbol}{product.price || 0}
                          </SelectItem>
                        )) : (
                          <SelectItem value="no-products" disabled>No products available</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="col-span-2">
                    <Label>{t('orders.create.quantity')}</Label>
                    <Input
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => handleQuantityChange(item.id, e.target.value)}
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>Unit Price</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.unit_price}
                      readOnly
                      className="bg-slate-50"
                    />
                  </div>
                  
                  <div className="col-span-2">
                    <Label>{t('orders.create.total')}</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={item.total_price}
                      readOnly
                      className="bg-slate-50 font-semibold"
                    />
                  </div>
                  
                  <div className="col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeItem(item.id)}
                      disabled={formData.items.length === 1}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
              
              <div className="flex justify-end pt-4 border-t">
                <div className="text-right space-y-2">
                   <div className="flex justify-between items-center gap-4">
                        <span className="text-slate-600">Items Total:</span>
                        <span className="font-semibold text-slate-900">{currencySymbol}{Number(formData.items.reduce((sum, item) => sum + (item.total_price || 0), 0)).toFixed(2)}</span>
                   </div>
                   <div className="flex justify-between items-center gap-4">
                        <span className="text-slate-600">Shipping:</span>
                        <span className="font-semibold text-slate-900">{currencySymbol}{Number(formData.shipping_cost || 0).toFixed(2)}</span>
                   </div>
                   <div className="flex justify-between items-center gap-4 border-t pt-2">
                        <span className="text-lg font-bold text-slate-900">Order Total:</span>
                        <span className="text-lg font-bold text-slate-900">{currencySymbol}{Number(calculateTotal()).toFixed(2)}</span>
                   </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Order Details */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <Label htmlFor="order_date">Order Date</Label>
                <Input
                  id="order_date"
                  type="date"
                  value={formData.order_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, order_date: e.target.value }))}
                />
              </div>
              
              <div>
                <Label htmlFor="payment_method">Payment Method</Label>
                <Select onValueChange={(value) => setFormData(prev => ({ ...prev, payment_method: value }))} value={formData.payment_method || undefined}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {normalizedPaymentMethods.length > 0 ? (
                      normalizedPaymentMethods.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="no-payment-methods" disabled>No payment methods available</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="shipping_cost">Shipping Cost</Label>
                <Alert className="mb-3">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription className="text-sm">
                    This form doesn't work until delivery method selection is supported. 
                    Use the public store checkout for proper delivery options.
                  </AlertDescription>
                </Alert>
                <Input
                  id="shipping_cost"
                  type="number"
                  step="0.01"
                  value={formData.shipping_cost}
                  onChange={(e) => setFormData(prev => ({ ...prev, shipping_cost: parseFloat(e.target.value) || 0 }))}
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="notes">Order Notes</Label>
              <Textarea
                id="notes"
                placeholder="Any special instructions or notes..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                className="h-32"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={
                isSubmitting ||
                ( !(formData.customer_id || (formData.customer_name && formData.customer_email)) ) ||
                formData.items.some(item => !item.product_id)
              }
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isSubmitting ? 'Creating...' : 'Create Order'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
